import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, createRootRouteWithContext, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { api, queries, type Me } from "@/api";
import { Avatar } from "@/components/Avatar";
import { BrandMark } from "@/components/BrandMark";
import { AfterReturn } from "@/components/AfterReturn";
import { HostBanners } from "@/components/HostBanners";
import { NotificationsDrawer, toggleNotifications, useNotificationsOpen } from "@/components/NotificationsDrawer";
import { SignIn } from "@/components/SignIn";
import { Splash, useSplash } from "@/components/Splash";
import { External } from "@/components/External";
import { useHost } from "@/host/host";
import { useReleaseLink } from "@/host/links";
import { hostQueries } from "@/host/queries";
import { setUpState } from "@/routes/setup";
import { syncHostSession } from "@/host/session";
import { pilotName } from "@/lib/format";
import { usePageHead } from "@/lib/head";
import { rememberSignedIn } from "@/lib/visitor";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  notFoundComponent: () => (
    <main className="page">
      <h1 className="text-2xl font-semibold">Page not found</h1>
      <p className="muted mt-2">
        <Link to="/" className="underline">
          Back to the fleet
        </Link>
      </p>
    </main>
  ),
});

/**
 * The shell (F413, decision 055; F456, decision 079): the navigation on the
 * left once signed in with the top bar over the page, a slim bar before. The page itself sits in the same place in every state,
 * so the answer to /me arriving never remounts it (a form being filled, a
 * card's note, stay as they are).
 */
function RootLayout() {
  const me = useQuery(queries.me());
  const signedIn = !!me.data;
  const pathname = useLocation().pathname;
  // The app's opening (2026-11-03): under the shell, the mark plays over the
  // page until the session is known and the chocks have landed.
  const host = useHost();
  const splash = useSplash(me.isPending);
  // The head follows the page (decision 136): the public pages described and canonical, the private ones out of the index.
  usePageHead(pathname, signedIn, me.isPending);
  // The next load of a public page starts blank for a pilot who signed in here, hydrated for a visitor.
  useEffect(() => {
    if (!me.isPending) rememberSignedIn(signedIn);
  }, [me.isPending, signedIn]);
  // The visitor's introduction runs the full width (its colour fields reach the edges, F501).
  const home = pathname === "/";
  return (
    <div className={signedIn ? "shell" : "flex min-h-screen flex-col"}>
      {host && splash !== "gone" ? <Splash phase={splash} /> : null}
      {signedIn ? <Sidebar me={me.data!} /> : me.isPending ? <BareBar /> : <TopBar />}
      <div className={signedIn ? "flex min-w-0 flex-1 flex-col" : "contents"}>
        {signedIn ? <WorkBar /> : null}
        {signedIn ? <AlphaBand me={me.data!} /> : null}
        {signedIn ? <NotificationsDrawer /> : null}
        <main className={signedIn ? "page flex-1" : home ? "page w-full flex-1 !max-w-none" : "page mx-auto w-full max-w-6xl flex-1"}>
          <HostContextMenu />
          <FirstPage />
          <WelcomeGate />
          <HostBanners />
          <AfterReturn />
          <Outlet />
        </main>
        {me.isPending ? null : <Footer />}
      </div>
    </div>
  );
}

/** The alpha, said once at the top of every signed-in page (decision 134): things break and get fixed, the bugs go to the crew room on Discord. */
function AlphaBand({ me }: { me: Me }) {
  const config = useQuery(queries.config());
  const invite = config.data?.discord_invite ?? null;
  return (
    <div className="alpha-band" data-testid="alpha-band">
      <span className="alpha-tag">Alpha</span>
      <span>Things break now and then and get fixed. Found one? Say it in the crew room on Discord.</span>
      {invite ? (
        <span className="ml-auto" data-testid="alpha-discord">
          <External href={invite} className="alpha-link">
            {me.in_crew_room ? "Open Discord" : "Join the server"}
          </External>
        </span>
      ) : null}
    </div>
  );
}

/** While the session is asked for: a shared link or the landing is never blank for a slow or failing /me. */
function BareBar() {
  return (
    <header className="topbar">
      <div className="mx-auto flex max-w-6xl items-center gap-2 px-6 py-3 font-bold tracking-tight">
        <BrandMark />
        ShariFly
      </div>
    </header>
  );
}

function Footer() {
  const me = useQuery(queries.me());
  // The download through the platform's own address, opened by the shell under the companion (decision 127).
  const companion = useReleaseLink("companion.msi");
  if (me.data) {
    return (
      <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 px-8 py-5 text-xs muted">
        <span>ShariFly, shared aircraft and fleet management for X-Plane 12.</span>
        <span>Nothing here changes your aircraft's files; the companion copies whole folders.</span>
        <Link to="/guide" className="underline">
          Help
        </Link>
        <Link to="/privacy" className="underline" data-testid="footer-privacy">
          How your data is kept
        </Link>
      </footer>
    );
  }
  // The visitor's footer (decision 089): the navy block of the reference, in columns.
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="mx-auto grid max-w-6xl gap-8 px-6 py-10 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2 font-bold">
            <BrandMark size={22} />
            ShariFly
          </div>
          <p className="text-[0.9286rem] opacity-80">Shared aircraft and fleet management for X-Plane 12. The Hot Start Challenger 650 is the only aircraft for now.</p>
        </div>
        <div>
          <span className="site-footer-title">The platform</span>
          <ul className="site-footer-links">
            <li><a href="/#how-it-works">How it works</a></li>
            <li><a href="/#the-record">The record</a></li>
            <li><a href="/#maintenance">Wear and failures</a></li>
            <li><a href="/#sharing">Sharing and privacy</a></li>
            <li><a href="/#questions">Questions</a></li>
          </ul>
        </div>
        <div>
          <span className="site-footer-title">Pilots</span>
          <ul className="site-footer-links">
            <li><Link to="/guide">Getting started</Link></li>
            <li>
              <External href={companion} className="">
                Download the app
              </External>
            </li>
            <li><a href="/#what-you-need">What you need</a></li>
            <li><a href="#sign-in">Sign in with Discord</a></li>
          </ul>
        </div>
        <div>
          <span className="site-footer-title">Trust</span>
          <ul className="site-footer-links">
            <li><a href="/#questions">Nothing in your aircraft's files is changed</a></li>
            <li><a href="/#questions">The platform is the only logbook</a></li>
            <li><a href="/#questions">Free until the end of 2026</a></li>
            <li><Link to="/privacy" data-testid="footer-privacy">How your data is kept</Link></li>
          </ul>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-6 pb-6 text-[0.9286rem] opacity-70">ShariFly is not affiliated with Hot Start, X-Aviation, Laminar Research or Bombardier.</div>
    </footer>
  );
}

function Icon({ name }: { name: "grid" | "plane" | "swap" | "bell" | "shield" | "book" | "pc" | "inbox" | "log" | "cog" | "gear" | "calendar" }) {
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, "aria-hidden": true as const };
  switch (name) {
    case "grid":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case "plane":
      return (
        <svg {...common}>
          <path transform="rotate(45 12 12)" d="M12 2.5l1.6 6.3L21 13v2l-7.2-1.6-.4 4.4 2.4 1.9V21l-3.8-1-3.8 1v-1.3l2.4-1.9-.4-4.4L3 15v-2l7.4-4.2z" strokeLinejoin="round" />
        </svg>
      );
    case "swap":
      return (
        <svg {...common}>
          <path d="M7 7h10l3 4-3 4H7z" />
          <path d="M4 17h13" />
        </svg>
      );
    case "gear":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      );
    case "bell":
      return (
        <svg {...common}>
          <path d="M6 8a6 6 0 0 1 12 0v5l2 3H4l2-3z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common}>
          <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />
        </svg>
      );
    case "book":
      return (
        <svg {...common}>
          <path d="M4 5h7a3 3 0 0 1 3 3v12a2 2 0 0 0-2-2H4z" />
          <path d="M20 5h-7a3 3 0 0 0-3 3v12a2 2 0 0 1 2-2h8z" />
        </svg>
      );
    case "pc":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="12" rx="1.5" />
          <path d="M8 20h8M12 16v4" />
        </svg>
      );
    case "inbox":
      return (
        <svg {...common}>
          <path d="M4 13l2-8h12l2 8v6H4z" />
          <path d="M4 13h5l1 2h4l1-2h5" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      );
    case "log":
      return (
        <svg {...common}>
          <path d="M6 3h9l4 4v14H6z" />
          <path d="M9 12h6M9 16h6" />
        </svg>
      );
    case "cog":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1" />
        </svg>
      );
  }
}

/** The rail items of the companion (decision 065): Set up until the pilot hides it (back when a step comes undone), and This PC; under the host alone. */
function ThisPcLinks() {
  const host = useHost();
  const info = useQuery({ ...hostQueries.info(host!), enabled: !!host });
  const status = useQuery({ ...hostQueries.status(host!), enabled: !!host });
  const me = useQuery(queries.me());
  if (!host) return null;
  const active = { className: "navlink navlink-active" };
  const session = info.data?.session ?? null;
  const setUp = info.data ? setUpState(info.data, status.data ?? [], !!me.data) : null;
  const showSetUp = !!setUp && (!info.data?.settings.setup_hidden || !setUp.finished);
  return (
    <>
      {showSetUp ? (
        <Link to="/setup" className="navlink justify-between" activeProps={active} data-testid="nav-setup">
          <span className="flex items-center gap-2.5">
            <Icon name="book" />
            Set up
          </span>
          <span className="nav-badge" data-testid="setup-count">
            {setUp.done} of {setUp.total}
          </span>
        </Link>
      ) : null}
      <Link to="/pc" className="navlink justify-between" activeProps={active} data-testid="nav-pc">
        <span className="flex items-center gap-2.5">
          <Icon name="pc" />
          This PC
        </span>
        {session ? <span className="dot text-ok" aria-label={session.watch ? "watching a flight" : "flying"} data-testid="nav-session" /> : null}
      </Link>
    </>
  );
}

/** Before you fly (F500, decision 110): a pilot who never went through it is sent there from any page but the notice itself and the help. */
function WelcomeGate() {
  const me = useQuery(queries.me());
  const location = useLocation();
  const navigate = useNavigate();
  const needs = !!me.data && !me.data.user.welcomed_at;
  const free = ["/welcome", "/privacy", "/guide"].some((p) => location.pathname.startsWith(p));
  useEffect(() => {
    if (needs && !free) navigate({ to: "/welcome" });
  }, [needs, free, navigate]);
  return null;
}

/** Under the host, a right-click offers Refresh alone (2026-11-03): the host pops the menu and the browser's own, with Back, Save as, Print and More tools, stays hidden. */
function HostContextMenu() {
  const host = useHost();
  useEffect(() => {
    if (!host) return;
    const onMenu = (e: MouseEvent) => {
      e.preventDefault();
      void host.contextMenu();
    };
    document.addEventListener("contextmenu", onMenu);
    return () => document.removeEventListener("contextmenu", onMenu);
  }, [host]);
  return null;
}

/** Under the host, the window opens on Set up until it is finished or hidden (decision 065): once per sign-in (a fresh install signs in from Welcome and lands there). */
function FirstPage() {
  const host = useHost();
  const navigate = useNavigate();
  const location = useLocation();
  const info = useQuery({ ...hostQueries.info(host!), enabled: !!host });
  const status = useQuery({ ...hostQueries.status(host!), enabled: !!host });
  const me = useQuery(queries.me());
  const [decidedFor, setDecidedFor] = useState<string | null>(null);
  const userId = me.data?.user.id ?? null;
  useEffect(() => {
    if (!host || !info.data || !status.data || me.isPending || userId === null || decidedFor === userId) return;
    setDecidedFor(userId);
    if (info.data.settings.setup_hidden) return;
    const s = setUpState(info.data, status.data, true);
    if (!s.finished && location.pathname === "/") navigate({ to: "/setup" });
  }, [decidedFor, host, info.data, status.data, me.isPending, userId, location.pathname, navigate]);
  return null;
}

function useLogout() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const host = useHost();
  return async () => {
    if (host) {
      // The shell ends the platform session and forgets the token; the
      // client is pointed at the platform again without one.
      await host.logout();
      await syncHostSession(host);
    } else {
      await api.POST("/api/v1/auth/logout");
    }
    queryClient.setQueryData(queries.me().queryKey, null);
    queryClient.removeQueries({ queryKey: ["notifications"] });
    router.navigate({ to: "/" });
  };
}

function Sidebar({ me }: { me: Me }) {
  const host = useHost();
  const logout = useLogout();
  const active = { className: "navlink navlink-active" };
  return (
    <aside className="sidebar">
      <Link to="/" className="brand flex items-center gap-2.5 text-base font-bold tracking-tight">
        <BrandMark />
        ShariFly
      </Link>
      {/* The navigation in sections (decision 079): the operations, this PC under the companion, the administration. */}
      <nav className="nav-section" aria-label="Operations">
        <span className="nav-group">Operations</span>
        <Link to="/" className="navlink" activeOptions={{ exact: true }} activeProps={active} data-testid="nav-fleet">
          <Icon name="grid" />
          Fleet
        </Link>
        <Link to="/logbook" search={{ scope: "mine", airframe: "", pilot: "", q: "", page: 1 }} className="navlink" activeProps={active} data-testid="nav-logbook">
          <Icon name="log" />
          Logbook
        </Link>
        <Link to="/schedule" search={{ day: "" }} className="navlink" activeProps={active} data-testid="nav-schedule">
          <Icon name="calendar" />
          Schedule
        </Link>
        <Link to="/airframes" search={{ scope: "public", q: "", joined: "exclude", continent: "", sort: "updated" }} className="navlink" activeProps={active} data-testid="nav-find">
          <Icon name="plane" />
          Marketplace
        </Link>
        <Link to="/transfers" className="navlink" activeProps={active} data-testid="nav-transfers">
          <Icon name="swap" />
          Transfers
        </Link>
      </nav>
      {host ? (
        <nav className="nav-section" aria-label="This PC">
          <span className="nav-group">This PC</span>
          <ThisPcLinks />
        </nav>
      ) : null}
      {me.user.is_admin ? (
        <nav className="nav-section" aria-label="Administration">
          <span className="nav-group">Administration</span>
          <Link to="/admin" search={{ tab: "users", q: "" }} className="navlink" activeProps={active} data-testid="nav-admin">
            <Icon name="shield" />
            Admin
          </Link>
        </nav>
      ) : null}
      <div className="mt-auto flex flex-col gap-2">
        {/* Settings at the bottom (decision 071): the reader's own. */}
        <nav className="nav-section" aria-label="Settings">
          <Link to="/settings" className="navlink" activeProps={active} data-testid="nav-settings">
            <Icon name="gear" />
            Settings
          </Link>
        </nav>
        <Link to="/account" className="nav-card" activeProps={{ className: "nav-card nav-card-active" }} data-testid="nav-account">
          <Avatar handle={me.user.handle} name={pilotName(me.user)} url={me.user.avatar_url} />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate font-semibold" data-testid="me-handle">
              {me.user.handle}
            </span>
            <span className="truncate text-xs opacity-70">{me.user.is_admin ? "Administrator" : pilotName(me.user)}</span>
          </span>
        </Link>
        <div className="flex gap-2">
          <Link to="/guide" className="btn btn-sm btn-nav flex-1 justify-center" data-testid="nav-guide">
            Help
          </Link>
          <button type="button" className="btn btn-sm btn-nav flex-1 justify-center" onClick={logout}>
            Sign out
          </button>
        </div>
      </div>
    </aside>
  );
}

/** The clock of the top bar: UTC, to the second, as the line keeps time. */
function useUtcClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now.toISOString().slice(11, 19);
}

/** Where the reader is, from the address (decision 079): the section, then the page; an airframe by its registration. */
function Crumbs() {
  const { pathname } = useLocation();
  const airframeId = pathname.match(/^\/airframes\/([^/]+)/)?.[1] ?? null;
  const airframe = useQuery({ ...queries.airframe(airframeId ?? ""), enabled: !!airframeId });
  const flightId = pathname.match(/^\/airframes\/[^/]+\/flights\/([^/]+)/)?.[1] ?? null;
  const flight = useQuery({ ...queries.flight(airframeId ?? "", flightId ?? ""), enabled: !!airframeId && !!flightId });
  const parts: { label: string; to?: string }[] = [];
  if (airframeId) {
    parts.push({ label: "Fleet", to: "/" });
    parts.push({ label: airframe.data?.identity.reg ?? "Airframe", to: `/airframes/${airframeId}` });
    const rest = pathname.slice(("/airframes/" + airframeId).length);
    if (rest.startsWith("/ledger")) parts.push({ label: "Logbook" });
    else if (rest.startsWith("/flights/"))
      parts.push(
        { label: "Logbook", to: `/airframes/${airframeId}/ledger` },
        { label: flight.data ? `${flight.data.flight.takeoff_icao || "----"} to ${flight.data.flight.landing_icao || "----"}` : "Flight" },
      );
    else if (rest.startsWith("/maintenance")) parts.push({ label: "Maintenance" });
    else if (rest.startsWith("/members")) parts.push({ label: "Members" });
    else if (rest.startsWith("/history")) parts.push({ label: "History" });
    else if (rest.startsWith("/settings")) parts.push({ label: "Settings" });
  } else if (pathname === "/") parts.push({ label: "Fleet" });
  else if (pathname.startsWith("/airframes")) parts.push({ label: "Marketplace" });
  else if (pathname.startsWith("/logbook")) parts.push({ label: "Logbook" });
  else if (pathname.startsWith("/transfers")) parts.push({ label: "Transfers" });
  else if (pathname.startsWith("/settings")) parts.push({ label: "Settings" });
  else if (pathname.startsWith("/account")) parts.push({ label: "Account" });
  else if (pathname.startsWith("/admin")) parts.push({ label: "Administration" });
  else if (pathname.startsWith("/setup")) parts.push({ label: "This PC", to: "/pc" }, { label: "Set up" });
  else if (pathname.startsWith("/pc")) {
    parts.push({ label: "This PC", to: "/pc" });
    if (pathname.startsWith("/pc/returns")) parts.push({ label: "Returns" });
    else if (pathname.startsWith("/pc/log")) parts.push({ label: "Log" });
    else if (pathname.startsWith("/pc/settings")) parts.push({ label: "Settings" });
  } else if (pathname.startsWith("/guide")) parts.push({ label: "Getting started" });
  else if (pathname.startsWith("/invites")) parts.push({ label: "Invitation" });
  if (parts.length === 0) return null;
  return (
    <nav className="crumbs" aria-label="Breadcrumb" data-testid="crumbs">
      {parts.map((p, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 ? <span aria-hidden="true">/</span> : null}
          {p.to && i < parts.length - 1 ? (
            <Link to={p.to}>{p.label}</Link>
          ) : (
            <span className={i === parts.length - 1 ? "crumb-here" : ""}>{p.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** The bar over every page once signed in (decision 079): where one is, a search of the airframes, the UTC clock, the bell, the pilot. */
function WorkBar() {
  const feed = useQuery(queries.notifications());
  const navigate = useNavigate();
  const clock = useUtcClock();
  const [q, setQ] = useState("");
  const unread = feed.data?.unread ?? 0;
  const drawerOpen = useNotificationsOpen();
  return (
    <header className="topbar">
      <div className="topbar-row">
        <Crumbs />
        <form
          className="topsearch ml-2"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ to: "/airframes", search: { scope: "public", q: q.trim(), joined: "include", continent: "", sort: "updated" } });
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search airframes by registration, name, airport" aria-label="Search airframes" data-testid="topsearch" />
        </form>
        <div className="ml-auto flex items-center gap-2">
          <LivePill />
          <span className="clock" data-testid="utc-clock" aria-label="UTC time">
            {clock}
            <span>UTC</span>
          </span>
          <button type="button" className={`iconbtn ${drawerOpen ? "iconbtn-active" : ""}`} aria-label={`Notifications, ${unread} unread`} aria-expanded={drawerOpen} title="Notifications" onClick={toggleNotifications} data-testid="nav-notifications">
            <Icon name="bell" />
            {unread > 0 ? (
              <span data-testid="unread-count" className="absolute -right-1.5 -top-1.5 rounded-full bg-accent px-1.5 text-xs font-bold text-white tnum">
                {unread}
              </span>
            ) : null}
          </button>
        </div>
      </div>
    </header>
  );
}

/** Before the sign-in, under the host: the way to This PC (the set-up, the settings). */
function ThisPcTopLink({ onClick }: { onClick?: () => void }) {
  const host = useHost();
  if (!host) return null;
  return (
    <Link to="/settings" className="hover:underline" activeProps={{ className: "text-accent" }} data-testid="nav-pc" onClick={onClick}>
      Settings
    </Link>
  );
}

/** Someone is flying (F471, decision 094): a pulse in the work bar naming the aircraft, the fleet desk behind it. */
function LivePill() {
  const hangar = useQuery({ ...queries.airframes("member", ""), refetchInterval: 30_000, staleTime: 30_000 });
  const flying = (hangar.data?.items ?? []).filter((a) => a.lease?.flying);
  if (flying.length === 0) return null;
  return (
    <Link to="/" className="live-pill" data-testid="live-pill" title={flying.map((a) => `${a.reg}, ${a.lease?.holder.name}`).join("; ")}>
      <span className="live-dot" aria-hidden="true" />
      {flying.length === 1 ? `${flying[0].reg} ${flying[0].lease?.position && !flying[0].lease.position.on_ground ? "in the air" : "being flown"}` : `${flying.length} being flown`}
    </Link>
  );
}

/** Before the sign-in (decision 089; redrawn in F508, decision 114): the wordmark, the pages in a row, the way in as the dark button; under 900 px the pages fold into a menu. */
function TopBar() {
  const router = useRouter();
  const next = router.state.location.href || "/";
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const links = (
    <>
      <a href="/#how-it-works" onClick={close}>
        How it works
      </a>
      <a href="/#the-record" onClick={close}>
        The record
      </a>
      <a href="/#questions" onClick={close}>
        Questions
      </a>
      <Link to="/guide" activeProps={{ className: "is-here" }} data-testid="nav-guide" onClick={close}>
        Getting started
      </Link>
      <ThisPcTopLink onClick={close} />
    </>
  );
  return (
    <header className="topbar topbar-site">
      <div className="topbar-site-row">
        <Link to="/" className="topbar-brand" aria-label="ShariFly, the home page" data-testid="wordmark">
          <BrandMark size={34} />
          <span>ShariFly</span>
        </Link>
        <nav className="topbar-site-nav" aria-label="Site">
          {links}
        </nav>
        <div className="topbar-site-actions" id="sign-in">
          <SignIn next={next} className="btn btn-dark" />
          <button type="button" className="topbar-menu" aria-expanded={open} aria-controls="site-menu" onClick={() => setOpen((o) => !o)} data-testid="site-menu">
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>
      {open ? (
        <nav id="site-menu" className="topbar-sheet" aria-label="Site, folded">
          {links}
        </nav>
      ) : null}
    </header>
  );
}
